import React, { useState } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Typography,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout as LogoutIcon,
  LockReset as LockResetIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { ChangePasswordDialog } from './ChangePasswordDialog';

interface TopBarProps {
  onMenuToggle?: () => void;
}

export const TopBar: React.FC<TopBarProps> = ({ onMenuToggle }) => {
  const { user, logout, isLoggingOut } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleOpenChangePassword = () => {
    handleMenuClose();
    setChangePasswordOpen(true);
  };

  const handleLogout = async () => {
    handleMenuClose();
    await logout();
    navigate('/login', { replace: true });
  };

  const getUserInitials = (): string => {
    if (!user?.fullName) return 'A';
    const names = user.fullName.split(' ').filter(Boolean);
    if (names.length === 1) {
      return names[0].substring(0, 2).toUpperCase();
    }
    return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        bgcolor: 'transparent',
        backdropFilter: 'blur(10px)',
        color: 'text.primary',
        zIndex: isMobile ? theme.zIndex.drawer - 1 : theme.zIndex.drawer + 1,
        paddingTop: 1,
      }}
    >
      <Toolbar>
        {isMobile && (
          <IconButton
            color="inherit"
            edge="start"
            onClick={onMenuToggle}
            sx={{ mr: 2 }}
            aria-label="Abrir menu"
          >
            <MenuIcon />
          </IconButton>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Box
          onClick={handleMenuOpen}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            cursor: 'pointer',
            px: 2,
            py: 1,
            borderRadius: 3,
            bgcolor: 'background.paper',
            transition: 'all 0.2s',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            '&:hover': {
              boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
            },
          }}
        >
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: 'primary.main',
              color: 'primary.contrastText',
              fontSize: '0.875rem',
              fontWeight: 600,
            }}
          >
            {getUserInitials()}
          </Avatar>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 500,
              color: 'text.primary',
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {user?.fullName}
          </Typography>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 20,
              color: 'text.secondary',
              ml: -0.5,
            }}
          />
        </Box>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          slotProps={{
            paper: {
              sx: { minWidth: 240, mt: 1 },
            },
          }}
        >
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              {user?.fullName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user?.email}
            </Typography>
          </Box>
          <Divider />
          <MenuItem onClick={handleOpenChangePassword}>
            <LockResetIcon sx={{ mr: 2 }} fontSize="small" />
            Trocar senha
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout} disabled={isLoggingOut} sx={{ color: 'error.main' }}>
            {isLoggingOut ? (
              <CircularProgress size={18} sx={{ mr: 2 }} />
            ) : (
              <LogoutIcon sx={{ mr: 2 }} fontSize="small" />
            )}
            Sair
          </MenuItem>
        </Menu>
      </Toolbar>

      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />
    </AppBar>
  );
};
